import React, { useCallback, useEffect, useState } from "react";
import Pagination from "react-js-pagination";
import { IoSearch } from "react-icons/io5";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import { toastMessage } from "../../utils/toastMessage";
import findSerialNumber from "../../utils/findSerialNumber";
import { Select } from "../../components/Form";
import {
  fetchRedemptionLogsApi,
  fetchRedemptionLogsSummaryApi,
} from "../../services/couponsV2";
import "./CouponsV2.css";

const ACTION_LABELS = {
  VALIDATE: "Validate",
  APPLY: "Apply",
  RECHARGE: "Recharge",
  FINALIZE: "Finalize",
};

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
};

const getUserLabel = (user) => {
  if (!user) return "—";
  const phone = user?.details?.phone || user?.phone;
  const name = user?.name || user?.email;
  if (name && phone) return `${name} (${phone})`;
  return name || phone || user?._id || "—";
};

export default function CouponRedemptionLogs() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [filters, setFilters] = useState({
    couponCode: "",
    action: "",
    success: "",
    from: "",
    to: "",
  });

  const pageSize = 15;

  const buildParams = useCallback(
    (page = activePage) => {
      const params = { page: page - 1, pageSize };
      if (filters.couponCode.trim()) params.couponCode = filters.couponCode.trim();
      if (filters.action) params.action = filters.action;
      if (filters.success !== "") params.success = filters.success;
      if (filters.from) params.from = new Date(filters.from).toISOString();
      if (filters.to) params.to = new Date(filters.to).toISOString();
      return params;
    },
    [activePage, filters],
  );

  const loadData = useCallback(async () => {
    showLoader();
    try {
      const params = buildParams();
      const summaryParams = { ...params };
      delete summaryParams.page;
      delete summaryParams.pageSize;

      const [logsRes, summaryRes] = await Promise.all([
        fetchRedemptionLogsApi(params),
        fetchRedemptionLogsSummaryApi(summaryParams),
      ]);

      if (logsRes.data.success) {
        setLogs(logsRes.data.data.list || []);
        setTotal(logsRes.data.data.pagination?.total || 0);
      }
      if (summaryRes.data.success) {
        setSummary(summaryRes.data.data);
      }
    } catch (e) {
      toastMessage("Failed to load redemption logs", "error");
    } finally {
      hideLoader();
    }
  }, [buildParams, hideLoader, showLoader]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (field, value) => {
    setActivePage(1);
    setFilters((f) => ({ ...f, [field]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setActivePage(1);
    loadData();
  };

  const totals = summary?.totals || { total: 0, success: 0, failed: 0 };
  const successRate = totals.total
    ? Math.round((totals.success / totals.total) * 100)
    : 0;

  return (
    <>
      {loader}
      <div className="cv2-summary-grid">
        <div className="cv2-stat-card">
          <span className="cv2-stat-label">Total Attempts</span>
          <strong className="cv2-stat-value">{totals.total}</strong>
        </div>
        <div className="cv2-stat-card cv2-stat-success">
          <span className="cv2-stat-label">Successful</span>
          <strong className="cv2-stat-value">{totals.success}</strong>
        </div>
        <div className="cv2-stat-card cv2-stat-failed">
          <span className="cv2-stat-label">Rejected</span>
          <strong className="cv2-stat-value">{totals.failed}</strong>
        </div>
        <div className="cv2-stat-card">
          <span className="cv2-stat-label">Success Rate</span>
          <strong className="cv2-stat-value">{successRate}%</strong>
        </div>
      </div>

      {(summary?.topFailures?.length > 0 || summary?.topCoupons?.length > 0) && (
        <div className="cv2-insights-row">
          {summary?.topFailures?.length > 0 && (
            <div className="cv2-insight-panel">
              <h6>Top Rejection Reasons</h6>
              <ul>
                {summary.topFailures.map((item) => (
                  <li key={item.reason}>
                    <span>{item.reason}</span>
                    <strong>{item.count}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {summary?.topCoupons?.length > 0 && (
            <div className="cv2-insight-panel">
              <h6>Most Attempted Coupons</h6>
              <ul>
                {summary.topCoupons.map((item) => (
                  <li key={item.couponCode}>
                    <span>{item.couponCode}</span>
                    <strong>
                      {item.success}/{item.total} ok
                    </strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <form className="FilterBox FilterArea" onSubmit={handleSearch}>
        <div className="form-group search_lg_size userSearch">
          <input
            type="search"
            className="form-control"
            placeholder="Filter by coupon code"
            value={filters.couponCode}
            onChange={(e) => handleFilterChange("couponCode", e.target.value)}
          />
          <span className="user_search_icon">
            <IoSearch />
          </span>
        </div>

        <div className="group_filter_value">
          <div className="form-group select_sm_width">
            <Select
              className="form-control"
              value={filters.action}
              onChange={(e) => handleFilterChange("action", e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="VALIDATE">Validate</option>
              <option value="APPLY">Apply</option>
              <option value="RECHARGE">Recharge</option>
              <option value="FINALIZE">Finalize</option>
            </Select>
          </div>
          <div className="form-group select_sm_width">
            <Select
              className="form-control"
              value={filters.success}
              onChange={(e) => handleFilterChange("success", e.target.value)}
            >
              <option value="">All Results</option>
              <option value="true">Success only</option>
              <option value="false">Rejected only</option>
            </Select>
          </div>
          <div className="form-group">
            <input
              type="datetime-local"
              className="form-control"
              value={filters.from}
              onChange={(e) => handleFilterChange("from", e.target.value)}
            />
          </div>
          <div className="form-group">
            <input
              type="datetime-local"
              className="form-control"
              value={filters.to}
              onChange={(e) => handleFilterChange("to", e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary" style={{ height: 38 }}>
            Apply Filters
          </button>
        </div>
      </form>

      <div className="content-table">
        <table>
          <thead>
            <tr>
              <th>S. No.</th>
              <th>Time</th>
              <th>Code</th>
              <th>Action</th>
              <th>User</th>
              <th>Consultant</th>
              <th>Context</th>
              <th>Device / Phone</th>
              <th>Result</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {logs.length > 0 ? (
              logs.map((log, index) => (
                <tr key={log._id}>
                  <td>{findSerialNumber(index, activePage, pageSize)}</td>
                  <td style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
                    {formatDate(log.createdAt)}
                  </td>
                  <td>
                    <strong style={{ color: "#CD2F38" }}>{log.couponCode}</strong>
                  </td>
                  <td>{ACTION_LABELS[log.action] || log.action}</td>
                  <td style={{ fontSize: "11px", maxWidth: 140 }}>
                    {getUserLabel(log.userId)}
                  </td>
                  <td style={{ fontSize: "11px" }}>
                    {log.consultantId?.accountName || "—"}
                  </td>
                  <td style={{ fontSize: "11px" }}>
                    {log.appliesOn || "—"}
                    {log.mode ? ` · ${log.mode}` : ""}
                  </td>
                  <td style={{ fontSize: "10px", maxWidth: 120 }}>
                    {log.deviceId && (
                      <div>
                        Dev:{" "}
                        {log.deviceId.length > 14
                          ? `${log.deviceId.slice(0, 14)}…`
                          : log.deviceId}
                      </div>
                    )}
                    {log.phone && <div>Ph: {log.phone}</div>}
                    {!log.deviceId && !log.phone && "—"}
                  </td>
                  <td>
                    <span
                      className={`cv2-badge ${log.success ? "cv2-badge-success" : "cv2-badge-failed"}`}
                    >
                      {log.success ? "Success" : "Rejected"}
                    </span>
                  </td>
                  <td style={{ fontSize: "11px", maxWidth: 180, color: "#666" }}>
                    {log.rejectionReason || "—"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="text-center py-4">
                  No redemption logs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > pageSize && (
        <div className="pagination-wrapper">
          <div className="Pagination">
            <Pagination
              activePage={activePage}
              itemsCountPerPage={pageSize}
              totalItemsCount={total}
              pageRangeDisplayed={5}
              onChange={(p) => setActivePage(p)}
              hideDisabled
            />
          </div>
        </div>
      )}
    </>
  );
}
